// ══════════════════════════════════════════════
//  DateInput — fecha propia del sistema (2026-09-10). Sustituye al
//  <input type="date"> del navegador, que se ve distinto en cada equipo y
//  no sabe nada del calendario laboral. Reglas:
//  - Teclear manda: el campo acepta dd/mm/aaaa con máscara (también d/m/aa,
//    ddmmaaaa o aaaa-mm-dd pegado) y emite el ISO canónico en cuanto la
//    fecha es real y trae el año completo; ↑/↓ mueven un día; Alt+↓ abre el
//    mes. El botón del calendario es apoyo, no el camino principal.
//  - El popover va por portal a Z.popover con MenuScrim (canon de menús):
//    mes con flechas, semana de lunes a domingo, hoy marcado, la fecha
//    elegida en primary y, con `cal` (calendarioDe / compilarCalendario de
//    calendario.logic), los feriados en ámbar y los no laborables atenuados.
//  - `compact` es la variante de celda de captura: el estilo del input lo
//    pone la tabla vía `inputStyle` y el botón mide 20 px.
//  - `soft` (2026-11-03) es el campo RELLENO de la calma, el mismo de
//    SearchInput soft (gb-field-soft, 30 px), con el calendario dentro.
//    Dentro de la burbuja es cápsula (hoja gb-base): la fecha de la
//    bitácora de acarreos.
//  Valor: 'YYYY-MM-DD' o ''. Nunca emite una fecha inválida.
// ══════════════════════════════════════════════
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { C, T, R, E, F, M, Z } from '../../design.js';
import { Icon } from './Icon.js';
import { MenuScrim } from './menus.js';
import { useEscToClose } from './hooks.js';
import { today } from '../../utils.js';
import { esLaborable } from '../../calendario.logic.js';
import {
  MESES, DIAS_CORTOS, esISO, partesISO, formatearFecha, mascaraFecha, parsearFecha,
  textoCompleto, celdasMes, mesRelativo, sumarDias,
} from './fecha.logic.js';

const ANCHO = 256;
const ALTO_ESTIMADO = 318;

const BotonMes = ({ icon, title, onClick }) => (
  <button type="button" onClick={onClick} title={title} aria-label={title}
    className="gb-ghost gb-press gb-i gb-focus"
    style={{ width: 26, height: 26, padding: 0, border: 'none', borderRadius: R.ctl,
      background: 'transparent', color: C.textMed, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
    <Icon name={icon} size={14} color="currentColor"/>
  </button>
);

const BotonPie = ({ children, onClick, disabled = false, primario = false }) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className={`${primario ? 'gb-tint' : 'gb-ghost'} gb-press gb-i gb-focus`}
    style={{ ...T.caption, fontWeight: 600, padding: '4px 10px', border: 'none', borderRadius: R.ctl,
      background: 'transparent', fontFamily: 'inherit',
      color: disabled ? C.textDisabled : primario ? C.primary : C.textMed,
      cursor: disabled ? 'default' : 'pointer' }}>
    {children}
  </button>
);

const DateInput = ({
  value, onChange, cal = null, label, required = false, error, placeholder = 'dd/mm/aaaa',
  disabled = false, compact = false, soft = false, inputRef, inputStyle = {}, style: sx = {},
  onKeyDown, onFocus, onBlur, title,
}) => {
  const [texto, setTexto] = useState(() => formatearFecha(value));
  const [open,  setOpen]  = useState(false);
  const [pos,   setPos]   = useState(null);
  const [vista, setVista] = useState(() => {
    const p = partesISO(esISO(value) ? value : today);
    return { anio: p.anio, mes: p.mes };
  });
  const editando = useRef(false);
  const wrapRef  = useRef(null);
  const inputEl  = useRef(null);

  // El texto sigue al valor salvo mientras se teclea
  useEffect(() => { if (!editando.current) setTexto(formatearFecha(value)); }, [value]);

  const setRefs = (el) => {
    inputEl.current = el;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) inputRef.current = el;
  };
  const emitir = (iso) => { if ((iso || '') !== (value || '')) onChange?.(iso || ''); };
  const fijar = (iso) => { editando.current = false; setTexto(formatearFecha(iso)); emitir(iso); };

  const alTeclear = (e) => {
    const crudo = e.target.value;
    editando.current = true;
    // Pegar un ISO se lee entero, sin pasar por la máscara
    const pegadoISO = esISO(crudo.trim()) ? parsearFecha(crudo.trim()) : null;
    const t = pegadoISO ? formatearFecha(pegadoISO) : mascaraFecha(crudo);
    setTexto(t);
    if (!t) { emitir(''); return; }
    if (textoCompleto(t)) { const v = parsearFecha(t); if (v) emitir(v); }
  };
  const alSalir = (e) => {
    editando.current = false;
    const v = texto ? parsearFecha(texto) : null;
    // Lo incompleto o inválido vuelve al último valor; vacío borra
    if (!texto) emitir('');
    setTexto(formatearFecha(v || (texto ? value : '')));
    if (v) emitir(v);
    onBlur?.(e);
  };
  const alTecla = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); abrir(); return; }
      e.preventDefault();
      fijar(sumarDias(esISO(value) ? value : today, e.key === 'ArrowUp' ? 1 : -1));
      return;
    }
    if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); return; }
    if (e.key === 'Enter') {
      // Enter acepta lo tecleado (también d/m/aa) antes de que el padre navegue
      const v = texto ? parsearFecha(texto) : null;
      if (v) fijar(v);
    }
    onKeyDown?.(e);
  };

  const abrir = () => {
    if (disabled) return;
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const vh = window.innerHeight, vw = window.innerWidth;
    const abajo = vh - r.bottom - 12 >= ALTO_ESTIMADO || (vh - r.bottom) >= r.top;
    const p = partesISO(esISO(value) ? value : today);
    setVista({ anio: p.anio, mes: p.mes });
    setPos({
      top:    abajo ? r.bottom + 4 : undefined,
      bottom: abajo ? undefined : vh - r.top + 4,
      left:   Math.max(8, Math.min(r.left, vw - ANCHO - 8)),
    });
    setOpen(true);
  };
  const cerrar = useCallback(() => setOpen(false), []);
  useEscToClose(open ? cerrar : null);

  const elegir = (iso) => {
    fijar(iso);
    setOpen(false);
    setTimeout(() => inputEl.current?.focus(), 0);
  };

  const celdas = useMemo(() => celdasMes(vista.anio, vista.mes), [vista]);

  const campo = (
    <div ref={wrapRef} style={{ position: 'relative', display: 'flex', alignItems: 'center',
      zIndex: open ? Z.popover : undefined, ...(label ? {} : sx) }}>
      <input ref={setRefs} value={texto} onChange={alTeclear} onKeyDown={alTecla}
        onFocus={onFocus} onBlur={alSalir}
        placeholder={placeholder} disabled={disabled} inputMode="numeric" maxLength={10}
        title={title} aria-invalid={error ? true : undefined}
        className={soft ? 'gb-i gb-focus gb-field-soft' : 'gb-i gb-focus'}
        style={compact
          ? { flex: 1, minWidth: 0, ...F.num, ...inputStyle }
          : soft
            // Relleno, borde y radio los pone gb-field-soft (al enfocarse vuelve a blanco): nada de eso va aquí.
            ? { flex: 1, minWidth: 0, boxSizing: 'border-box', height: 30, padding: '0 30px 0 12px',
                fontSize: 13, color: C.text, fontFamily: 'inherit', ...F.num,
                ...(error ? { borderColor: C.errorFieldBorder, background: C.errorField } : null), ...inputStyle }
            : { flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '8px 30px 8px 10px',
                borderRadius: R.ctl, border: `1px solid ${error ? C.errorFieldBorder : C.border}`,
                fontSize: 13, color: C.text, fontFamily: 'inherit', ...F.num,
                background: error ? C.errorField : C.surface, ...inputStyle }}/>
      <button type="button" tabIndex={-1} disabled={disabled}
        onClick={() => (open ? setOpen(false) : abrir())}
        title="Elegir en el calendario" aria-label="Elegir en el calendario"
        aria-haspopup="dialog" aria-expanded={open}
        className="gb-ghost gb-press gb-i"
        style={{ position: compact ? 'static' : 'absolute', right: compact ? undefined : soft ? 3 : 4,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: compact ? 20 : 24, height: compact ? 20 : 24, padding: 0, border: 'none',
          borderRadius: soft ? R.round : R.xs, flexShrink: 0,
          background: open ? C.primaryLight : 'transparent',
          color: open ? C.primary : C.textMuted, cursor: disabled ? 'default' : 'pointer' }}>
        <Icon name="calendar" size={compact ? 12 : 14} color="currentColor"/>
      </button>
    </div>
  );

  // `data-gb-popover` (2026-11-02): el calendario va por portal al body; un menú que lo hospeda (el periodo del
  // Resumen de acarreos en un MenuCinta) no lo toma por un clic de afuera: cambiar de mes ya no cierra el menú.
  const panel = open && pos && createPortal(
    <div data-gb-popover="">
      <MenuScrim onClose={cerrar} zIndex={Z.popover - 1}/>
      <div role="dialog" aria-label="Calendario"
        style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: ANCHO,
          zIndex: Z.popover, background: C.flotante, border: `1px solid ${C.border}`,
          borderRadius: R.menu, boxShadow: E.menu, padding: 10, boxSizing: 'border-box',
          animation: `gbFade ${M.fast} ${M.ease}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
          <BotonMes icon="chevron-left" title="Mes anterior"
            onClick={() => setVista(v => mesRelativo(v.anio, v.mes, -1))}/>
          <span style={{ flex: 1, textAlign: 'center', ...T.caption, fontWeight: 700, color: C.text,
            textTransform: 'capitalize' }}>
            {MESES[vista.mes - 1]} {vista.anio}
          </span>
          <BotonMes icon="chevron-right" title="Mes siguiente"
            onClick={() => setVista(v => mesRelativo(v.anio, v.mes, 1))}/>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {DIAS_CORTOS.map(d => (
            <div key={d} style={{ textAlign: 'center', ...T.micro, fontWeight: 600, color: C.textMuted, padding: '2px 0' }}>{d}</div>
          ))}
          {celdas.map(c => {
            const sel = c.iso === value, esHoy = c.iso === today;
            const feriado = !!cal?.feriados?.has(c.iso);
            const noLab = !!cal && !feriado && !esLaborable(c.iso, cal);
            return (
              <button key={c.iso} type="button" onClick={() => elegir(c.iso)}
                className={sel ? 'gb-press gb-i gb-focus' : 'gb-ghost gb-press gb-i gb-focus'}
                title={feriado ? 'Feriado' : noLab ? 'No laborable' : undefined}
                style={{ height: 28, padding: 0, borderRadius: R.ctl, cursor: 'pointer',
                  border: `1px solid ${esHoy && !sel ? C.primary : 'transparent'}`,
                  fontFamily: 'inherit', fontSize: 12, ...F.num,
                  fontWeight: sel || esHoy ? 700 : 500,
                  background: sel ? C.primaryFill : 'transparent',
                  color: sel ? C.onPrimary
                    : !c.delMes ? C.textDisabled
                    : feriado ? C.warning
                    : noLab ? C.textMuted
                    : C.text }}>
                {c.dia}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <BotonPie onClick={() => elegir('')} disabled={!value}>Borrar</BotonPie>
          <BotonPie onClick={() => elegir(today)} primario>Hoy</BotonPie>
        </div>
      </div>
    </div>,
    document.body,
  );

  if (label) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, ...sx }}>
        <label style={{ ...T.caption, color: C.textSub }}>
          {label}{required && <span style={{ color: C.danger }}> *</span>}
        </label>
        {campo}
        {typeof error === 'string' && error && <span style={{ ...T.micro, color: C.danger }}>{error}</span>}
        {panel}
      </div>
    );
  }
  return <>{campo}{panel}</>;
};

export { DateInput };
